<script setup>
/* global axios */
// Disparador — wizard de criação de campanha (4 passos): origem, configurações, mensagens, revisão.
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;
const apiBase = `/api/v1/accounts/${accountId}`;

const step = ref(1);
const STEPS = [1, 2, 3, 4];
const submitting = ref(false);

// passo 1 — origem
const form = ref({
  name: '',
  description: '',
  source_type: 'csv',
  source_config: {},
  inbox_ids: [],
  rotation_mode: 'round_robin',
  delay_min_seconds: 5,
  delay_max_seconds: 20,
  pause_every_n: null,
  pause_for_seconds: null,
  window_start: '',
  window_end: '',
  allowed_days: [],
  enable_spintax: false,
  simulate_typing: false,
  scheduled_at: '',
  inbox_weights: {},
  inbox_messages: {},
  follow_up_enabled: false,
  follow_up_config: { waitHours: 24, message: '' },
  is_recurring: false,
  recurring_interval_days: 7,
  recurring_end_date: '',
  messages: [{ type: 'text', content: '', mediaUrl: '', delayAfterSeconds: 0 }],
});
const perInbox = ref(false);
const activeInboxTab = ref(null);
const blankMsg = () => ({
  type: 'text',
  content: '',
  mediaUrl: '',
  delayAfterSeconds: 0,
});
const selectTab = id => {
  const key = id?.toString();
  activeInboxTab.value = key;
  if (key && !form.value.inbox_messages[key])
    form.value.inbox_messages[key] = [blankMsg()];
};
const messagesForTab = computed(() => {
  if (!perInbox.value || !activeInboxTab.value) return form.value.messages;
  return form.value.inbox_messages[activeInboxTab.value] || form.value.messages;
});
const csvContacts = ref([]);
const csvPreview = ref([]);
const inboxes = ref([]);
const funnels = ref([]);
const labels = ref([]);
const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const SOURCES = [
  {
    id: 'csv',
    icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  },
  { id: 'sanitize', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
  {
    id: 'tags',
    icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  },
  {
    id: 'kanban_stage',
    icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2',
  },
];

const selectedFunnel = ref(null);
const stagesOfFunnel = computed(
  () => funnels.value.find(f => f.id === selectedFunnel.value)?.stages || []
);

// início do disparo: imediato x agendado
const startMode = ref('immediate');
// filtro de canal das caixas de envio
const channelFilter = ref('all');
const shortChannel = ib =>
  (ib.channel_type || '').replace('Channel::', '').replace('Api', 'API');
const channelTypes = computed(() => [
  ...new Set(inboxes.value.map(shortChannel)),
]);
const filteredInboxes = computed(() =>
  channelFilter.value === 'all'
    ? inboxes.value
    : inboxes.value.filter(ib => shortChannel(ib) === channelFilter.value)
);

const loadAux = async () => {
  try {
    inboxes.value = (await axios.get(`${apiBase}/inboxes`)).data.payload || [];
  } catch (e) {
    inboxes.value = [];
  }
  try {
    funnels.value = (await axios.get(`${base}/funnels`)).data.data || [];
  } catch (e) {
    funnels.value = [];
  }
  try {
    labels.value = (await axios.get(`${apiBase}/labels`)).data.payload || [];
  } catch (e) {
    labels.value = [];
  }
};

const onCsv = async e => {
  const file = e.target.files[0];
  if (!file) return;
  const fd = new FormData();
  fd.append('file', file);
  const { data } = await axios.post(`${base}/campaigns/upload_csv`, fd);
  csvContacts.value = data.contacts || [];
  csvPreview.value = data.preview || [];
};

const toggle = (arr, val) => {
  const i = arr.indexOf(val);
  if (i >= 0) arr.splice(i, 1);
  else arr.push(val);
};
const toggleStage = id => {
  form.value.source_config.stage_ids = form.value.source_config.stage_ids || [];
  toggle(form.value.source_config.stage_ids, id);
};
const toggleLabel = title => {
  form.value.source_config.tags = form.value.source_config.tags || [];
  toggle(form.value.source_config.tags, title);
};
const addMessage = () => {
  const list = messagesForTab.value;
  if (list.length < 5)
    list.push({
      type: 'text',
      content: '',
      mediaUrl: '',
      delayAfterSeconds: 5,
    });
};
const removeMessage = i => messagesForTab.value.splice(i, 1);
const onPerInbox = () => {
  if (perInbox.value && !activeInboxTab.value)
    selectTab(form.value.inbox_ids[0]);
};

const sourceReady = computed(() => {
  if (!form.value.name.trim()) return false;
  if (form.value.source_type === 'csv') return csvContacts.value.length > 0;
  if (form.value.source_type === 'kanban_stage')
    return (form.value.source_config.stage_ids || []).length > 0;
  if (form.value.source_type === 'tags')
    return (form.value.source_config.tags || []).length > 0;
  if (form.value.source_type === 'sanitize') return false;
  return true;
});
const settingsReady = computed(() => form.value.inbox_ids.length > 0);
const hasContent = list =>
  (list || []).some(m => m.content.trim() || m.mediaUrl.trim());
const messagesReady = computed(() => {
  if (perInbox.value)
    return form.value.inbox_ids.every(id =>
      hasContent(form.value.inbox_messages[id?.toString()])
    );
  return hasContent(form.value.messages);
});
const canNext = computed(() => {
  if (step.value === 1) return sourceReady.value;
  if (step.value === 2) return settingsReady.value;
  if (step.value === 3) return messagesReady.value;
  return true;
});

const inboxName = id => inboxes.value.find(i => i.id === id)?.name || `#${id}`;

const submit = async startNow => {
  submitting.value = true;
  try {
    const payload = { ...form.value };
    const scheduled = startMode.value === 'scheduled' && payload.scheduled_at;
    if (!scheduled) delete payload.scheduled_at;
    if (scheduled) payload.status = 'scheduled';
    if (!payload.recurring_end_date) delete payload.recurring_end_date;
    if (!perInbox.value) payload.inbox_messages = {};
    const { data } = await axios.post(`${base}/campaigns`, payload);
    const id = data.data.id;
    if (form.value.source_type === 'csv' && csvContacts.value.length) {
      await axios.post(`${base}/campaigns/${id}/import_contacts`, {
        contacts: csvContacts.value,
      });
    }
    // agendado: o cron inicia na hora marcada; imediato: inicia agora se pedido
    if (startNow && !scheduled)
      await axios.post(`${base}/campaigns/${id}/start`);
    router.push({
      name: 'kanban_campaign_detail',
      params: { accountId, campaignId: id },
    });
  } finally {
    submitting.value = false;
  }
};

onMounted(loadAux);
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <header class="flex items-center gap-3 px-6 py-4 border-b border-n-weak">
      <button
        class="p-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
        @click="
          router.push({ name: 'kanban_campaigns', params: { accountId } })
        "
      >
        <svg
          class="w-5 h-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <h1 class="text-heading-1 text-n-slate-12">
        {{ t('DISPARADOR.newCampaign') }}
      </h1>
      <div class="flex items-center ml-auto">
        <template v-for="(s, idx) in STEPS" :key="s">
          <span
            class="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full"
            :class="
              step === s
                ? 'bg-n-brand text-white'
                : step > s
                  ? 'text-n-brand'
                  : 'text-n-slate-10'
            "
          >
            <span
              class="flex items-center justify-center w-5 h-5 text-[0.625rem] font-semibold rounded-full"
              :class="
                step === s
                  ? 'bg-white/25'
                  : step > s
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2'
              "
              >{{ s }}</span
            >
            {{ t(`DISPARADOR.wizard.step${s}`) }}
          </span>
          <span
            v-if="idx < STEPS.length - 1"
            class="w-5 mx-0.5 border-t border-dashed border-n-weak"
          />
        </template>
      </div>
    </header>

    <div class="flex-1 p-6 overflow-y-auto">
      <div class="max-w-2xl mx-auto">
        <!-- passo 1: origem -->
        <template v-if="step === 1">
          <label class="block mb-1 text-sm font-medium text-n-slate-12"
            >{{ t('DISPARADOR.wizard.nameLabel')
            }}<span class="text-n-ruby-11"> *</span></label
          >
          <input
            v-model="form.name"
            class="w-full px-3 py-2 mb-4 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('DISPARADOR.wizard.namePlaceholderEx')"
          />
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.descriptionLabel')
          }}</label>
          <textarea
            v-model="form.description"
            rows="2"
            class="w-full px-3 py-2 mb-4 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            :placeholder="t('DISPARADOR.descriptionPlaceholder')"
          />
          <label class="block mb-2 text-sm font-medium text-n-slate-12"
            >{{ t('DISPARADOR.wizard.sourceQ')
            }}<span class="text-n-ruby-11"> *</span></label
          >
          <div class="grid grid-cols-2 gap-2 mb-4 md:grid-cols-4">
            <button
              v-for="src in SOURCES"
              :key="src.id"
              class="flex flex-col items-start gap-1 p-3 text-left border rounded-lg"
              :class="
                form.source_type === src.id
                  ? 'border-n-brand bg-n-alpha-1'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
              @click="form.source_type = src.id"
            >
              <svg
                class="w-5 h-5 text-n-slate-11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  :d="src.icon"
                />
              </svg>
              <span class="text-sm font-medium text-n-slate-12">{{
                t(`DISPARADOR.wizard.src.${src.id}`)
              }}</span>
              <span class="text-[0.6875rem] text-n-slate-10">{{
                t(`DISPARADOR.wizard.srcDesc.${src.id}`)
              }}</span>
            </button>
          </div>

          <div v-if="form.source_type === 'csv'">
            <div
              class="flex flex-wrap items-center gap-1 px-3 py-2 mb-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-10"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {{ t('DISPARADOR.wizard.csvCols') }}
              <code class="text-n-blue-11">{{ 'telefone' }}</code>
              {{ t('DISPARADOR.wizard.csvOpt') }}
              <code class="text-n-blue-11">{{ 'nome' }}</code>
              {{ t('DISPARADOR.wizard.csvOtherVars') }}
              <a
                href="data:text/csv;charset=utf-8,telefone,nome%0A5511999999999,Fulano"
                download="modelo-disparo.csv"
                class="ml-auto text-n-brand hover:underline"
                >{{ t('DISPARADOR.wizard.downloadTemplate') }}</a
              >
            </div>
            <label
              class="flex flex-col items-center justify-center gap-1 px-4 py-8 text-sm border border-dashed rounded-lg cursor-pointer border-n-weak text-n-slate-10 hover:bg-n-alpha-1"
            >
              <svg
                class="w-6 h-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3-3m0 0l3 3m-3-3v12"
                />
              </svg>
              {{ t('DISPARADOR.wizard.dropCsv') }}
              <input
                type="file"
                accept=".csv,.txt"
                class="hidden"
                @change="onCsv"
              />
            </label>
            <p v-if="csvContacts.length" class="mt-2 text-sm text-n-teal-11">
              {{ t('DISPARADOR.wizard.csvLoaded', { n: csvContacts.length }) }}
            </p>
            <div v-if="csvPreview.length" class="mt-2 text-xs text-n-slate-10">
              <div v-for="(r, i) in csvPreview" :key="i">
                {{ r.phone }} — {{ r.name || '—' }}
              </div>
            </div>
          </div>

          <p
            v-else-if="form.source_type === 'sanitize'"
            class="py-6 text-sm text-center text-n-slate-10"
          >
            {{ t('DISPARADOR.sanitizeSoon') }}
          </p>

          <div v-else-if="form.source_type === 'kanban_stage'">
            <select
              v-model="selectedFunnel"
              class="w-full pl-3 pr-9 py-2 mb-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11"
            >
              <option :value="null">
                {{ t('DISPARADOR.wizard.selectFunnel') }}
              </option>
              <option v-for="f in funnels" :key="f.id" :value="f.id">
                {{ f.name }}
              </option>
            </select>
            <label
              v-for="s in stagesOfFunnel"
              :key="s.id"
              class="flex items-center gap-2 py-1 text-sm text-n-slate-11"
            >
              <input
                type="checkbox"
                :checked="(form.source_config.stage_ids || []).includes(s.id)"
                @change="toggleStage(s.id)"
              />
              {{ s.name }}
            </label>
          </div>

          <div v-else-if="form.source_type === 'tags'">
            <button
              v-for="l in labels"
              :key="l.id"
              class="inline-flex items-center gap-1 px-2.5 py-1 mb-1 mr-1 text-xs rounded-full border"
              :class="
                (form.source_config.tags || []).includes(l.title)
                  ? 'border-n-brand text-n-brand'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggleLabel(l.title)"
            >
              {{ l.title }}
            </button>
          </div>
        </template>

        <!-- passo 2: configurações -->
        <template v-else-if="step === 2">
          <label class="block mb-2 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.wizard.boxes')
          }}</label>
          <div v-if="channelTypes.length > 1" class="flex gap-1 mb-2">
            <button
              v-for="ch in ['all', ...channelTypes]"
              :key="ch"
              class="px-2.5 py-1 text-xs rounded-md border"
              :class="
                channelFilter === ch
                  ? 'border-n-brand text-n-brand bg-n-alpha-1'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="channelFilter = ch"
            >
              {{ ch === 'all' ? t('DISPARADOR.allStatuses') : ch }}
            </button>
          </div>
          <label
            v-for="ib in filteredInboxes"
            :key="ib.id"
            class="flex items-center gap-3 px-3 py-2 mb-1.5 text-sm border rounded-lg cursor-pointer"
            :class="
              form.inbox_ids.includes(ib.id)
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
          >
            <input
              type="checkbox"
              :checked="form.inbox_ids.includes(ib.id)"
              @change="toggle(form.inbox_ids, ib.id)"
            />
            <div>
              <div class="font-medium text-n-slate-12">{{ ib.name }}</div>
              <div class="text-[0.6875rem] text-n-slate-10">
                {{ `${shortChannel(ib)} · ID ${ib.id}` }}
              </div>
            </div>
          </label>
          <label class="block mt-4 mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.wizard.rotation')
          }}</label>
          <div class="flex gap-2 mb-4">
            <button
              v-for="r in ['round_robin', 'random', 'weighted']"
              :key="r"
              class="px-3 py-1.5 text-sm rounded-lg border"
              :class="
                form.rotation_mode === r
                  ? 'border-n-brand text-n-brand bg-n-alpha-1'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="form.rotation_mode = r"
            >
              {{ t(`DISPARADOR.wizard.rot.${r}`) }}
            </button>
          </div>
          <div class="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.delayMin')
              }}</label>
              <input
                v-model.number="form.delay_min_seconds"
                type="number"
                min="1"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.delayMax')
              }}</label>
              <input
                v-model.number="form.delay_max_seconds"
                type="number"
                min="1"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.windowStart')
              }}</label>
              <input
                v-model="form.window_start"
                type="time"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.windowEnd')
              }}</label>
              <input
                v-model="form.window_end"
                type="time"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>
          <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
            t('DISPARADOR.wizard.allowedDays')
          }}</label>
          <div class="flex gap-1 mb-4">
            <button
              v-for="d in DAYS"
              :key="d"
              class="px-2.5 py-1 text-xs rounded-md border"
              :class="
                form.allowed_days.includes(d)
                  ? 'border-n-brand text-n-brand bg-n-alpha-1'
                  : 'border-n-weak text-n-slate-11'
              "
              @click="toggle(form.allowed_days, d)"
            >
              {{ t(`DISPARADOR.wizard.day.${d}`) }}
            </button>
          </div>
          <p
            class="pt-4 mt-4 mb-2 text-[0.6875rem] font-semibold tracking-wider uppercase border-t text-n-slate-10 border-n-weak"
          >
            {{ t('DISPARADOR.wizard.antiBlock') }}
          </p>
          <label class="flex items-start gap-2 text-sm text-n-slate-11">
            <input
              v-model="form.enable_spintax"
              type="checkbox"
              class="mt-0.5"
            />
            <span>
              {{ t('DISPARADOR.wizard.spintax') }}
              <span class="block text-[0.6875rem] text-n-slate-10">{{
                t('DISPARADOR.wizard.spintaxHint')
              }}</span>
            </span>
          </label>

          <!-- pesos por inbox (rotação ponderada) -->
          <div v-if="form.rotation_mode === 'weighted'" class="mt-4">
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.wizard.weights')
            }}</label>
            <div
              v-for="id in form.inbox_ids"
              :key="id"
              class="flex items-center gap-2 mb-1"
            >
              <span class="text-sm text-n-slate-11 w-40 truncate">{{
                inboxName(id)
              }}</span>
              <input
                v-model.number="form.inbox_weights[id]"
                type="number"
                min="1"
                class="w-20 px-2 py-1 text-sm rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>

          <!-- pausa periódica -->
          <div class="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.pauseEvery')
              }}</label>
              <input
                v-model.number="form.pause_every_n"
                type="number"
                min="0"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.pauseFor')
              }}</label>
              <input
                v-model.number="form.pause_for_seconds"
                type="number"
                min="0"
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>

          <label class="flex items-center gap-2 mt-4 text-sm text-n-slate-11"
            ><input v-model="form.simulate_typing" type="checkbox" />
            {{ t('DISPARADOR.wizard.simulateTyping') }}</label
          >

          <!-- follow-up -->
          <label class="flex items-start gap-2 mt-4 text-sm text-n-slate-11">
            <input
              v-model="form.follow_up_enabled"
              type="checkbox"
              class="mt-0.5"
            />
            <span>
              {{ t('DISPARADOR.wizard.followUp') }}
              <span class="block text-[0.6875rem] text-n-slate-10">{{
                t('DISPARADOR.wizard.followUpHint')
              }}</span>
            </span>
          </label>
          <div
            v-if="form.follow_up_enabled"
            class="p-3 mt-2 rounded-lg border border-n-weak"
          >
            <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
              t('DISPARADOR.wizard.followUpWait')
            }}</label>
            <input
              v-model.number="form.follow_up_config.waitHours"
              type="number"
              min="1"
              class="w-24 px-2 py-1 mb-2 text-sm rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
            />
            <textarea
              v-model="form.follow_up_config.message"
              rows="2"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.wizard.followUpMessage')"
            />
          </div>

          <!-- recorrência -->
          <label class="flex items-center gap-2 mt-4 text-sm text-n-slate-11"
            ><input v-model="form.is_recurring" type="checkbox" />
            {{ t('DISPARADOR.wizard.recurring') }}</label
          >
          <div
            v-if="form.is_recurring"
            class="flex items-center gap-3 p-3 mt-2 rounded-lg border border-n-weak"
          >
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.recurringEvery')
              }}</label>
              <input
                v-model.number="form.recurring_interval_days"
                type="number"
                min="1"
                class="w-24 px-2 py-1 text-sm rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
            <div>
              <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
                t('DISPARADOR.wizard.recurringEnd')
              }}</label>
              <input
                v-model="form.recurring_end_date"
                type="date"
                class="px-2 py-1 text-sm rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>

          <!-- início do disparo -->
          <p
            class="pt-4 mt-4 mb-2 text-[0.6875rem] font-semibold tracking-wider uppercase border-t text-n-slate-10 border-n-weak"
          >
            {{ t('DISPARADOR.wizard.startSection') }}
          </p>
          <div class="grid grid-cols-2 gap-2">
            <button
              v-for="mode in ['immediate', 'scheduled']"
              :key="mode"
              class="flex flex-col items-center gap-1 p-3 text-center border rounded-lg"
              :class="
                startMode === mode
                  ? 'border-n-brand bg-n-alpha-1'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
              @click="startMode = mode"
            >
              <svg
                class="w-5 h-5 text-n-slate-11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  v-if="mode === 'immediate'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span class="text-sm font-medium text-n-slate-12">{{
                t(
                  `DISPARADOR.wizard.start${mode === 'immediate' ? 'Now' : 'Scheduled'}`
                )
              }}</span>
              <span class="text-[0.6875rem] text-n-slate-10">{{
                t(
                  `DISPARADOR.wizard.start${mode === 'immediate' ? 'Now' : 'Scheduled'}Desc`
                )
              }}</span>
            </button>
          </div>
          <input
            v-if="startMode === 'scheduled'"
            v-model="form.scheduled_at"
            type="datetime-local"
            class="px-3 py-2 mt-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
          />
        </template>

        <!-- passo 3: mensagens -->
        <template v-else-if="step === 3">
          <p class="mb-3 text-xs text-n-slate-10">
            {{ t('DISPARADOR.wizard.varsHint') }}
          </p>
          <label
            v-if="form.inbox_ids.length > 1"
            class="flex items-center gap-2 mb-3 text-sm text-n-slate-11"
            ><input v-model="perInbox" type="checkbox" @change="onPerInbox" />
            {{ t('DISPARADOR.wizard.perInbox') }}</label
          >
          <div v-if="perInbox" class="flex gap-1 mb-3 border-b border-n-weak">
            <button
              v-for="id in form.inbox_ids"
              :key="id"
              class="px-3 py-1.5 text-sm border-b-2 border-transparent text-n-slate-10"
              :class="{
                '!text-n-brand !border-n-brand':
                  activeInboxTab === id.toString(),
              }"
              @click="selectTab(id)"
            >
              {{ inboxName(id) }}
            </button>
          </div>
          <div
            v-for="(m, i) in messagesForTab"
            :key="i"
            class="p-3 mb-3 rounded-lg border border-n-weak"
          >
            <div class="flex items-center gap-2 mb-2">
              <select
                v-model="m.type"
                class="pl-2 pr-8 py-1 text-xs rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-11"
              >
                <option value="text">
                  {{ t('DISPARADOR.wizard.type.text') }}
                </option>
                <option value="image">
                  {{ t('DISPARADOR.wizard.type.image') }}
                </option>
                <option value="document">
                  {{ t('DISPARADOR.wizard.type.document') }}
                </option>
                <option value="video">
                  {{ t('DISPARADOR.wizard.type.video') }}
                </option>
                <option value="audio">
                  {{ t('DISPARADOR.wizard.type.audio') }}
                </option>
              </select>
              <span class="text-xs text-n-slate-10">{{
                t('DISPARADOR.wizard.messageN', { n: i + 1 })
              }}</span>
              <button
                v-if="messagesForTab.length > 1"
                class="ml-auto p-1 rounded-md text-n-ruby-11 hover:bg-n-ruby-3"
                @click="removeMessage(i)"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path stroke-linecap="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              v-if="m.type !== 'text'"
              v-model="m.mediaUrl"
              class="w-full px-3 py-2 mb-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.wizard.mediaUrl')"
            />
            <textarea
              v-model="m.content"
              rows="3"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12"
              :placeholder="t('DISPARADOR.wizard.messagePlaceholder')"
            />
            <div
              v-if="i > 0"
              class="flex items-center gap-2 mt-2 text-xs text-n-slate-10"
            >
              {{ t('DISPARADOR.wizard.delayAfter') }}
              <input
                v-model.number="m.delayAfterSeconds"
                type="number"
                min="0"
                class="w-16 px-2 py-1 rounded-md border border-n-weak bg-n-alpha-1 text-n-slate-12"
              />
            </div>
          </div>
          <button
            v-if="messagesForTab.length < 5"
            class="text-sm text-n-brand hover:underline"
            @click="addMessage"
          >
            + {{ t('DISPARADOR.wizard.addMessage') }}
          </button>
        </template>

        <!-- passo 4: revisão -->
        <template v-else>
          <div class="p-4 space-y-2 text-sm rounded-lg border border-n-weak">
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{ t('DISPARADOR.col.name') }}</span
              ><span class="font-medium text-n-slate-12">{{ form.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{
                t('DISPARADOR.col.source')
              }}</span
              ><span class="text-n-slate-12">{{
                t(`DISPARADOR.wizard.src.${form.source_type}`)
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{
                t('DISPARADOR.wizard.inboxes')
              }}</span
              ><span class="text-n-slate-12">{{
                form.inbox_ids.map(inboxName).join(', ')
              }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-n-slate-10">{{
                t('DISPARADOR.col.progress')
              }}</span
              ><span class="text-n-slate-12">{{
                `${form.delay_min_seconds}–${form.delay_max_seconds}s`
              }}</span>
            </div>
            <div class="flex flex-wrap gap-1 pt-1">
              <span
                v-if="form.enable_spintax"
                class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-alpha-2 text-n-slate-11"
                >{{ 'spintax' }}</span
              >
              <span
                v-if="form.window_start"
                class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-alpha-2 text-n-slate-11"
                >{{ form.window_start }}–{{ form.window_end }}</span
              >
              <span
                v-if="form.scheduled_at"
                class="px-2 py-0.5 text-[0.625rem] rounded-full bg-n-blue-3 text-n-blue-11"
                >{{ t('DISPARADOR.wizard.scheduled') }}</span
              >
            </div>
          </div>
        </template>
      </div>
    </div>

    <footer
      class="flex items-center justify-between px-6 py-4 border-t border-n-weak"
    >
      <button
        v-if="step > 1"
        class="px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="step -= 1"
      >
        {{ t('DISPARADOR.wizard.back') }}
      </button>
      <span v-else />
      <div class="flex gap-2">
        <button
          v-if="step < 4"
          class="px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
          :disabled="!canNext"
          @click="step += 1"
        >
          {{ t('DISPARADOR.wizard.next') }}
        </button>
        <template v-else>
          <button
            class="px-3.5 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1 disabled:opacity-60"
            :disabled="submitting"
            @click="submit(false)"
          >
            {{ t('DISPARADOR.wizard.saveDraft') }}
          </button>
          <button
            class="px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110 disabled:opacity-60"
            :disabled="submitting"
            @click="submit(true)"
          >
            {{ t('DISPARADOR.wizard.createStart') }}
          </button>
        </template>
      </div>
    </footer>
  </div>
</template>
